import React, { useState } from 'react';
import { useBuilding } from '../../context/BuildingContext';
import { useAuth } from '@clerk/clerk-react';
import Spinner from '../../components/common/Spinner';
import ErrorAlert from '../../components/common/ErrorAlert';

interface CustomReportProps {
  period: string;
}

interface SavedReport {
  id: string;
  name: string;
  description: string;
  createdBy: string;
  createdAt: string;
  lastRun?: string;
  reportType: 'financial' | 'service-charge' | 'maintenance' | 'unit' | 'custom';
  parameters: Record<string, any>;
}

interface ReportField {
  id: string;
  name: string;
  source: string;
  type: string;
}

const CustomReport: React.FC<CustomReportProps> = ({ period }) => {
  const { currentBuilding } = useBuilding();
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedReports, setSavedReports] = useState<SavedReport[]>([
    {
      id: 'report1',
      name: 'Monthly Financial Summary',
      description: 'Overview of income and expenses for each month',
      createdBy: 'John Admin',
      createdAt: '2023-02-15T10:30:00Z',
      lastRun: '2023-05-01T14:25:00Z',
      reportType: 'financial',
      parameters: {
        groupBy: 'month',
        includeCharts: true,
        showCategories: true
      }
    },
    {
      id: 'report2',
      name: 'Payment Status by Unit',
      description: 'Service charge payment status for each unit',
      createdBy: 'Jane Manager',
      createdAt: '2023-03-10T09:15:00Z',
      lastRun: '2023-04-28T11:05:00Z',
      reportType: 'service-charge',
      parameters: {
        showUnpaidOnly: false,
        includeLatePayments: true,
        sortBy: 'unit'
      }
    },
    {
      id: 'report3',
      name: 'Maintenance Cost Analysis',
      description: 'Breakdown of maintenance costs by category and priority',
      createdBy: 'John Admin',
      createdAt: '2023-04-05T15:45:00Z',
      lastRun: '2023-04-20T16:30:00Z',
      reportType: 'maintenance',
      parameters: {
        groupBy: 'category',
        includeSuppliers: true,
        showCompletedOnly: false
      }
    }
  ]);
  
  const [reportName, setReportName] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [reportType, setReportType] = useState('financial');
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [groupBy, setGroupBy] = useState('month');
  const [sortBy, setSortBy] = useState('date');
  const [includeCharts, setIncludeCharts] = useState(true);

  // Available fields for reports
  const availableFields: ReportField[] = [
    { id: 'date', name: 'Date', source: 'transactions', type: 'date' },
    { id: 'category', name: 'Category', source: 'transactions', type: 'string' },
    { id: 'amount', name: 'Amount', source: 'transactions', type: 'number' },
    { id: 'description', name: 'Description', source: 'transactions', type: 'string' },
    { id: 'unitId', name: 'Unit', source: 'units', type: 'string' },
    { id: 'paymentStatus', name: 'Payment Status', source: 'payments', type: 'string' },
    { id: 'maintenanceType', name: 'Maintenance Type', source: 'maintenance', type: 'string' },
    { id: 'priority', name: 'Priority', source: 'maintenance', type: 'string' },
    { id: 'supplierName', name: 'Supplier', source: 'suppliers', type: 'string' },
    { id: 'budgetedAmount', name: 'Budgeted Amount', source: 'budget', type: 'number' },
    { id: 'variance', name: 'Variance', source: 'calculations', type: 'number' },
  ];

  const handleFieldToggle = (fieldId: string) => {
    setSelectedFields(prev => 
      prev.includes(fieldId)
        ? prev.filter(id => id !== fieldId)
        : [...prev, fieldId]
    );
  };

  const handleSaveReport = () => {
    if (!reportName) {
      setError('Please provide a report name');
      return;
    }

    setLoading(true);
    
    // In a real app, you would save to an API
    // setTimeout to simulate API call
    setTimeout(() => {
      const newReport: SavedReport = {
        id: `report${Date.now()}`,
        name: reportName,
        description: reportDescription,
        createdBy: 'Current User',
        createdAt: new Date().toISOString(),
        reportType: reportType as any,
        parameters: {
          fields: selectedFields,
          groupBy,
          sortBy,
          includeCharts
        }
      };
      
      setSavedReports(prev => [newReport, ...prev]);
      
      // Reset form
      setReportName('');
      setReportDescription('');
      setSelectedFields([]);
      setGroupBy('month');
      setSortBy('date');
      setIncludeCharts(true);
      
      setLoading(false);
    }, 1000);
  };

  const handleGenerateReport = (reportId: string) => {
    setGenerating(true);
    
    // In a real app, you would generate the report via API
    // setTimeout to simulate API call
    setTimeout(() => {
      alert('Report generated! In a real app, this would generate a PDF or display results.');
      setGenerating(false);
      
      // Update last run timestamp
      setSavedReports(prev => 
        prev.map(report => 
          report.id === reportId 
            ? { ...report, lastRun: new Date().toISOString() }
            : report
        )
      );
    }, 2000);
  };

  const handleDeleteReport = (reportId: string) => {
    // Use window.confirm instead of global confirm to avoid eslint no-restricted-globals
    if (window.confirm('Are you sure you want to delete this report?')) {
      setSavedReports(prev => prev.filter(report => report.id !== reportId));
    }
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-6">Custom Reports</h2>
      
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        {/* Saved Reports Section */}
        <div className="lg:col-span-2">
          <div className="bg-white shadow rounded-lg mb-8">
            <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Saved Reports</h3>
            </div>
            <div className="px-4 py-5 sm:p-6">
              {savedReports.length > 0 ? (
                <ul className="divide-y divide-gray-200">
                  {savedReports.map((report) => (
                    <li key={report.id} className="py-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="text-lg font-medium text-gray-900">{report.name}</h4>
                          <p className="mt-1 text-sm text-gray-500">{report.description}</p>
                          <div className="mt-2 text-xs text-gray-500">
                            <span>Created by {report.createdBy} on {new Date(report.createdAt).toLocaleDateString()}</span>
                            {report.lastRun && (
                              <span className="ml-4">Last run: {new Date(report.lastRun).toLocaleDateString()}</span>
                            )}
                          </div>
                          <div className="mt-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                              ${
                                report.reportType === 'financial' ? 'bg-primary-100 text-primary-800' :
                                report.reportType === 'service-charge' ? 'bg-green-100 text-green-800' :
                                report.reportType === 'maintenance' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-purple-100 text-purple-800'
                              }
                            `}>
                              {report.reportType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4 flex-shrink-0 flex">
                          <button
                            type="button"
                            onClick={() => handleGenerateReport(report.id)}
                            disabled={generating}
                            className="mr-2 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none disabled:opacity-50"
                          >
                            {generating ? 'Generating...' : 'Generate'}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteReport(report.id)}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-center py-6">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No saved reports</h3>
                  <p className="mt-1 text-sm text-gray-500">Create a new custom report to get started.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Create New Report Form */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Create New Report</h3>
            </div>
            <div className="px-4 py-5 sm:p-6">
              {error && (
                <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              )}
              <form className="space-y-4">
                <div>
                  <label htmlFor="reportName" className="block text-sm font-medium text-gray-700">
                    Report Name
                  </label>
                  <input
                    type="text"
                    name="reportName"
                    id="reportName"
                    value={reportName}
                    onChange={(e) => setReportName(e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="Monthly Financial Summary"
                  />
                </div>
                
                <div>
                  <label htmlFor="reportDescription" className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                    name="reportDescription"
                    id="reportDescription"
                    rows={2}
                    value={reportDescription}
                    onChange={(e) => setReportDescription(e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="Brief description of the report"
                  ></textarea>
                </div>
                
                <div>
                  <label htmlFor="reportType" className="block text-sm font-medium text-gray-700">
                    Report Type
                  </label>
                  <select
                    id="reportType"
                    name="reportType"
                    value={reportType}
                    onChange={(e) => setReportType(e.target.value)}
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                  >
                    <option value="financial">Financial</option>
                    <option value="service-charge">Service Charge</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="unit">Units</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Select Fields
                  </label>
                  <div className="mt-2 space-y-2">
                    {availableFields.map((field) => (
                      <div key={field.id} className="flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            id={`field-${field.id}`}
                            name={`field-${field.id}`}
                            type="checkbox"
                            checked={selectedFields.includes(field.id)}
                            onChange={() => handleFieldToggle(field.id)}
                            className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label htmlFor={`field-${field.id}`} className="font-medium text-gray-700">
                            {field.name}
                          </label>
                          <p className="text-gray-500 text-xs">{field.source}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="groupBy" className="block text-sm font-medium text-gray-700">
                      Group By
                    </label>
                    <select
                      id="groupBy"
                      name="groupBy"
                      value={groupBy}
                      onChange={(e) => setGroupBy(e.target.value)}
                      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                    >
                      <option value="none">None</option>
                      <option value="day">Day</option>
                      <option value="week">Week</option>
                      <option value="month">Month</option>
                      <option value="quarter">Quarter</option>
                      <option value="year">Year</option>
                      <option value="category">Category</option>
                      <option value="unit">Unit</option>
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="sortBy" className="block text-sm font-medium text-gray-700">
                      Sort By
                    </label>
                    <select
                      id="sortBy"
                      name="sortBy"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                    >
                      <option value="date">Date</option>
                      <option value="amount">Amount</option>
                      <option value="category">Category</option>
                      <option value="unit">Unit</option>
                    </select>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="includeCharts"
                      name="includeCharts"
                      type="checkbox"
                      checked={includeCharts}
                      onChange={(e) => setIncludeCharts(e.target.checked)}
                      className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="includeCharts" className="font-medium text-gray-700">
                      Include Charts
                    </label>
                    <p className="text-gray-500">Generate visualizations for the report data</p>
                  </div>
                </div>
                
                <div className="pt-4">
                  <button
                    type="button"
                    onClick={handleSaveReport}
                    disabled={loading}
                    className="w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : 'Save Report'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomReport;
